import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { DrivePicker } from "@/components/DrivePicker";
import { restartAndWait } from "@/lib/localServer";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Server, Loader2, CheckCircle2 } from "lucide-react";

const STEPS = ["Storage", "Link to cloud", "Download data"];

function StepBar({ step }) {
  return (
    <div className="flex gap-2 mb-8" data-testid="setup-steps">
      {STEPS.map((s, i) => (
        <div key={s} className="flex-1">
          <div className={`h-1 ${i <= step ? "bg-primary" : "bg-muted"}`} />
          <div className={`text-[10px] uppercase tracking-[0.2em] mt-2 ${i === step ? "text-primary" : "text-muted-foreground"}`}>{i + 1}. {s}</div>
        </div>
      ))}
    </div>
  );
}

function ConnectStep({ onDone, prefill }) {
  const [url, setUrl] = useState("");
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/local/connect", prefill ? {} : { cloud_url: url.trim(), sync_key: key.trim() });
      toast.success(`Linked to ${data.org_name}`);
      onDone();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  if (prefill) return (
    <div className="space-y-4">
      <div className="text-sm">This installer already contains your cloud link:</div>
      <div className="font-mono text-sm border border-border p-3 break-all" data-testid="setup-prefill-url">{prefill}</div>
      <Button onClick={go} disabled={busy} data-testid="setup-connect-btn">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Link and download data</Button>
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">On the cloud ERP, the owner opens Settings → Shop Server &amp; Sync → Link shop server, and gets these two values.</div>
      <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em]">Cloud address</Label>
        <Input type="url" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://your-erp-address" data-testid="setup-cloud-url" /></div>
      <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em]">Sync key</Label>
        <Input type="password" value={key} onChange={e => setKey(e.target.value)} placeholder="smk_…" data-testid="setup-sync-key" /></div>
      <Button onClick={go} disabled={busy || !url || !key} data-testid="setup-connect-btn">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Link and download data</Button>
    </div>
  );
}

function DownloadStep({ st }) {
  const snap = st?.snapshot || {};
  const done = snap.status === "done";
  return (
    <div className="space-y-4" data-testid="setup-download-step">
      {snap.status === "failed" ? <div className="text-sm text-destructive">Download failed: {snap.error}. Restart setup to try again.</div> : (
        <div className="flex items-center gap-3 text-sm">
          {done ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Loader2 className="w-5 h-5 animate-spin text-primary" />}
          <span data-testid="setup-download-status">{done ? `All ${snap.total} records downloaded for ${st.org_name}.` : `Downloading ${st?.org_name || ""} data… ${snap.done || 0}/${snap.total || "?"}`}</span>
        </div>
      )}
      {done && <Button onClick={() => { window.location.href = "/login"; }} data-testid="setup-finish-btn">Go to login</Button>}
      {done && <div className="text-xs text-muted-foreground">Sign in with the same email and password you use on the cloud ERP.</div>}
    </div>
  );
}

export default function LocalSetup() {
  const [st, setSt] = useState(null);
  const [step, setStep] = useState(0);
  const [restarting, setRestarting] = useState(false);
  const onStorageSaved = async (res) => {
    if (res.restart_required) {
      setRestarting(true);
      const ok = await restartAndWait();
      setRestarting(false);
      if (!ok) { toast.error("Server did not come back yet — refresh this page in a minute"); return; }
    }
    setStep(1);
  };
  useEffect(() => {
    let first = true;
    const load = () => api.get("/local/public-status").then(r => {
      setSt(r.data);
      if (!r.data.setup_required) setStep(2);
      else if (first && r.data.storage_configured && !r.data.restart_required) setStep(1);
      first = false;
    }).catch(() => {});
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="local-setup-page">
      <div className="w-full max-w-xl border border-border bg-card p-6 sm:p-10">
        <div className="flex items-center gap-3 mb-2"><Server className="w-5 h-5 text-primary" /><span className="text-primary text-xs uppercase tracking-[0.3em]">Shop server setup</span></div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter mb-2">SynqMoto on this computer</h1>
        <p className="text-sm text-muted-foreground mb-8">This computer will be the main server for your shop. Everyone on the shop WiFi/LAN connects to it, and it syncs with the cloud every minute.</p>
        <StepBar step={step} />
        {step === 0 && (restarting
          ? <div className="text-sm flex items-center gap-2" data-testid="setup-restarting"><Loader2 className="w-4 h-4 animate-spin text-primary" />Preparing the storage folder, restarting the server…</div>
          : <><div className="text-sm mb-4">Choose where the ERP data is stored on this computer.</div><DrivePicker onSaved={onStorageSaved} /></>)}
        {step === 1 && <ConnectStep onDone={() => setStep(2)} prefill={st?.prefill_cloud_url} />}
        {step === 2 && <DownloadStep st={st} />}
      </div>
    </div>
  );
}
